import { initReactI18next } from 'react-i18next';

import * as Platform from '@actual-app/core/shared/platform';
import i18n from 'i18next';
import resourcesToBackend from 'i18next-resources-to-backend';

import { languageOverrides, languages } from './languages';

// Idioma por defecto cuando el usuario no ha elegido ninguno
// (las pruebas de Playwright siguen esperando la interfaz en inglés).
// En pruebas (vitest/Playwright) la interfaz se queda en inglés: los tests
// upstream buscan textos en inglés. En la app real, castellano.
export const DEFAULT_LANGUAGE =
  Platform.isPlaywright || import.meta.env?.MODE === 'test' ? 'en' : 'es';

/**
 * Idiomas de Rumbo (Vadik, 16-oct-2026): castellano y catalán, nada más.
 * Las demás traducciones de upstream se ignoran aunque estén en `locale/`.
 */
export const IDIOMAS_RUMBO = ['es', 'ca'];

/** Cada idioma se nombra en su propia lengua, como lo dice quien lo habla. */
export const NOMBRES_IDIOMAS: Record<string, string> = {
  es: 'Castellano',
  ca: 'Català',
};

const SOLO_IDIOMAS_RUMBO = DEFAULT_LANGUAGE !== 'en';

/** ¿Se puede usar este idioma en Rumbo? (en pruebas, todos los de `locale/`). */
export function esIdiomaDeRumbo(
  language: string,
  soloRumbo = SOLO_IDIOMAS_RUMBO,
): boolean {
  return !soloRumbo || IDIOMAS_RUMBO.includes(language);
}

const tieneTraduccion = (language: string) =>
  Object.hasOwn(languages, `/locale/${language}.json`) ||
  Object.hasOwn(languageOverrides, `/src/locale-overrides/${language}.json`);

const isLanguageAvailable = (language: string) =>
  esIdiomaDeRumbo(language) && tieneTraduccion(language);

export const availableLanguages = Platform.isPlaywright
  ? []
  : SOLO_IDIOMAS_RUMBO
    ? IDIOMAS_RUMBO.filter(tieneTraduccion)
    : Object.keys(languages).map(path => path.split('/')[2].split('.')[0]);

/**
 * Idioma de «Predeterminado del sistema»: catalán si el móvil está en
 * catalán (o lo prefiere antes que el castellano); si no, castellano.
 */
export function idiomaDelSistema(
  preferidos: readonly string[] = typeof navigator === 'undefined'
    ? []
    : (navigator.languages ?? [navigator.language]),
): string {
  for (const preferido of preferidos) {
    const base = (preferido ?? '').toLowerCase().split('-')[0];
    if (base === 'ca' || base === 'es') {
      return base;
    }
  }
  return 'es';
}

type Translations = Record<string, string>;

/**
 * Carga la traducción de upstream y, si existe, le superpone la traducción
 * propia de `src/locale-overrides/<idioma>.json` (misma clave, texto nuevo).
 * Así se corrigen o añaden cadenas sin tocar los archivos de upstream.
 */
export const loadLanguage = async (language: string): Promise<Translations> => {
  if (!isLanguageAvailable(language)) {
    throw new Error(`Unknown locale ${language}`);
  }
  // Los JSON cargados con import.meta.glob llegan como módulo ({ default }).
  // Sin traducción de upstream (p. ej. compilado sin `locale/`), solo la propia.
  const cargar = languages[`/locale/${language}.json`];
  const loaded = (cargar ? await cargar() : {}) as {
    default?: Translations;
  };
  const base: Translations = loaded.default ?? (loaded as Translations);
  const overrides = languageOverrides[`/src/locale-overrides/${language}.json`];
  if (!overrides) {
    return base;
  }
  return { ...base, ...overrides };
};

void i18n
  .use(initReactI18next)
  .use(resourcesToBackend(loadLanguage))
  .init({
    lng: DEFAULT_LANGUAGE,

    // allow keys to be phrases having `:`, `.`
    nsSeparator: false,
    keySeparator: false,
    // Lo que aún no esté en catalán se ve en castellano, no en inglés.
    fallbackLng: { ca: ['es'] },
    interpolation: {
      escapeValue: false,
    },
    react: {
      transSupportBasicHtmlNodes: false,
    },
  });

const resolveLanguage = (language: string) => {
  // English is always available since we use natural-language keys.
  if (language === 'en') return SOLO_IDIOMAS_RUMBO ? undefined : 'en';

  if (isLanguageAvailable(language)) return language;

  const lowercaseLanguage = language.toLowerCase();
  if (lowercaseLanguage !== language) {
    console.info(
      `Unknown locale ${language}, falling back to ${lowercaseLanguage}`,
    );
    return resolveLanguage(lowercaseLanguage);
  }

  if (language.includes('-')) {
    const fallback = language.split('-')[0];
    console.info(`Unknown locale ${language}, falling back to ${fallback}`);
    return resolveLanguage(fallback);
  }

  return undefined;
};

export const setI18NextLanguage = (language: string | null) => {
  // Sin idioma elegido se usa el del sistema (catalán o castellano); si
  // faltara, el inglés. En pruebas, el inglés.
  const porDefecto =
    DEFAULT_LANGUAGE === 'en' ? DEFAULT_LANGUAGE : idiomaDelSistema();
  let resolved = language ? resolveLanguage(language) : undefined;

  if (!resolved) {
    // Un idioma guardado que Rumbo ya no ofrece (p. ej. inglés) pasa al del
    // sistema; si tampoco estuviera, al inglés.
    resolved = resolveLanguage(porDefecto) ?? 'en';
    console.info(
      language
        ? `Unknown locale ${language}, falling back to ${resolved}`
        : `No locale chosen, using ${resolved}`,
    );
  }

  if (resolved === i18n.language) {
    return; // language is already set
  }

  void i18n.changeLanguage(resolved);
};
